import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin, CheckCircle, AlertCircle, X, Info, Sparkles, Camera, List, LayoutGrid, Upload } from 'lucide-react';
import { MobileLayout } from '../components/MobileLayout';
type EventStatus = 'confermato' | 'oggi' | 'completato';
interface CalendarEvent {
  id: string;
  reqId: string;
  timeStart: string;
  timeEnd: string;
  zone: string;
  service: string;
  status: EventStatus;
  duration: string;
}
interface DaySchedule {
  id: string;
  date: string;
  dayName: string;
  dayNumber: string;
  events: CalendarEvent[];
}
export function PianificazionePage() {
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  // Modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  // Form states
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  // Mock data for supplier schedule
  const scheduleData: DaySchedule[] = [{
    id: '1',
    date: '2024-01-22',
    dayName: 'Lun',
    dayNumber: '22',
    events: [{
      id: 'evt-1',
      reqId: '#REQ-2024-0156',
      timeStart: '10:00',
      timeEnd: '13:00',
      zone: 'Milano - Navigli',
      service: 'Pulizia standard',
      status: 'confermato',
      duration: '3h'
    }]
  }, {
    id: '2',
    date: '2024-01-23',
    dayName: 'Mar',
    dayNumber: '23',
    events: [{
      id: 'evt-2',
      reqId: '#REQ-2024-0157',
      timeStart: '09:00',
      timeEnd: '13:00',
      zone: 'Milano - Centro',
      service: 'Deep cleaning',
      status: 'oggi',
      duration: '4h'
    }]
  }, {
    id: '3',
    date: '2024-01-24',
    dayName: 'Mer',
    dayNumber: '24',
    events: [{
      id: 'evt-3',
      reqId: '#REQ-2024-0158',
      timeStart: '14:00',
      timeEnd: '17:00',
      zone: 'Milano - Porta Venezia',
      service: 'Pulizia standard',
      status: 'confermato',
      duration: '3h'
    }]
  }, {
    id: '4',
    date: '2024-01-25',
    dayName: 'Gio',
    dayNumber: '25',
    events: [{
      id: 'evt-4',
      reqId: '#REQ-2024-0155',
      timeStart: '11:00',
      timeEnd: '12:30',
      zone: 'Milano - Brera',
      service: 'Cambio biancheria',
      status: 'completato',
      duration: '1.5h'
    }]
  }, {
    id: '5',
    date: '2024-01-26',
    dayName: 'Ven',
    dayNumber: '26',
    events: []
  }, {
    id: '6',
    date: '2024-01-27',
    dayName: 'Sab',
    dayNumber: '27',
    events: []
  }, {
    id: '7',
    date: '2024-01-28',
    dayName: 'Dom',
    dayNumber: '28',
    events: []
  }];
  const hours = Array.from({
    length: 13
  }, (_, i) => i + 8); // 8:00 to 20:00
  // Helper to calculate position
  const getPositionStyle = (timeStart: string, timeEnd: string) => {
    const startHour = parseInt(timeStart.split(':')[0]);
    const startMin = parseInt(timeStart.split(':')[1]);
    const startTotalMinutes = (startHour - 8) * 60 + startMin;
    const endHour = parseInt(timeEnd.split(':')[0]);
    const endMin = parseInt(timeEnd.split(':')[1]);
    const endTotalMinutes = (endHour - 8) * 60 + endMin;
    const durationMinutes = endTotalMinutes - startTotalMinutes;
    const topPercent = startTotalMinutes / 720 * 100;
    const heightPercent = durationMinutes / 720 * 100;
    return {
      top: `${topPercent}%`,
      height: `${heightPercent}%`
    };
  };
  const getStatusColor = (status: EventStatus) => {
    switch (status) {
      case 'confermato':
        return 'bg-green-100 border-green-300 text-green-800';
      case 'oggi':
        return 'bg-amber-100 border-amber-300 text-amber-800 ring-2 ring-amber-200';
      case 'completato':
        return 'bg-gray-100 border-gray-200 text-gray-600';
      default:
        return 'bg-gray-100 border-gray-200 text-gray-600';
    }
  };
  const getStatusBadge = (status: EventStatus) => {
    switch (status) {
      case 'confermato':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-455">
            Confermato
          </span>;
      case 'oggi':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-456">
            Da completare oggi
          </span>;
      case 'completato':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-457">
            Completato
          </span>;
    }
  };
  const getStatusDot = (status: EventStatus) => {
    switch (status) {
      case 'confermato':
        return 'bg-green-500';
      case 'oggi':
        return 'bg-amber-500';
      case 'completato':
        return 'bg-gray-400';
    }
  };
  return <MobileLayout data-id="element-458">
      {/* Page Header */}
      <div className="mb-6 lg:mb-8" data-id="element-459">
        <h1 className="text-xl lg:text-2xl font-montserrat font-bold text-navy" data-id="element-460">
          Pianificazione
        </h1>
        <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-461">
          Gestisci i tuoi interventi programmati
        </p>
      </div>

      {/* View Toggle Tabs */}
      <div className="flex p-1 bg-gray-100 rounded-lg w-fit mb-6" data-id="element-462">
        <button onClick={() => setViewMode('calendar')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'calendar' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-463">
          <LayoutGrid size={16} data-id="element-464" />
          Calendario
        </button>
        <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-465">
          <List size={16} data-id="element-466" />
          Lista
        </button>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-6" data-id="element-467">
        <div className="flex items-center gap-2" data-id="element-468">
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200" data-id="element-469">
            <ChevronLeft size={20} className="text-gray-600" data-id="element-470" />
          </button>
          <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg" data-id="element-471">
            <CalendarIcon size={16} className="text-teal-deep" data-id="element-472" />
            <span className="text-sm font-bold text-navy" data-id="element-473">
              22 - 28 Gen 2024
            </span>
          </div>
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200" data-id="element-474">
            <ChevronRight size={20} className="text-gray-600" data-id="element-475" />
          </button>
        </div>

        <button className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-navy hover:bg-gray-50" data-id="element-476">
          Oggi
        </button>
      </div>

      {/* Content Views */}
      {viewMode === 'calendar' ? <>
          {/* Gantt Chart */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-id="element-477">
            <div className="overflow-x-auto" data-id="element-478">
              <div className="min-w-[800px]" data-id="element-479">
                {/* Header Row */}
                <div className="flex border-b border-gray-200 bg-gray-50" data-id="element-480">
                  <div className="w-16 flex-shrink-0 border-r border-gray-200 sticky left-0 bg-gray-50 z-10" data-id="element-481"></div>
                  {scheduleData.map(day => <div key={day.date} className="flex-1 py-3 text-center border-r border-gray-200 last:border-r-0" data-id="element-482">
                      <div className="text-xs text-gray-500 font-medium uppercase" data-id="element-483">
                        {day.dayName}
                      </div>
                      <div className="text-sm font-bold text-navy" data-id="element-484">
                        {day.dayNumber}
                      </div>
                    </div>)}
                </div>

                {/* Grid */}
                <div className="relative" data-id="element-485">
                  {/* Hours Background */}
                  <div className="divide-y divide-gray-100" data-id="element-486">
                    {hours.map(hour => <div key={hour} className="flex h-16" data-id="element-487">
                        <div className="w-16 flex-shrink-0 border-r border-gray-200 flex items-start justify-center pt-1 sticky left-0 bg-white z-10" data-id="element-488">
                          <span className="text-xs text-gray-400" data-id="element-489">
                            {hour}:00
                          </span>
                        </div>
                        <div className="flex-1 bg-white" data-id="element-490"></div>
                      </div>)}
                  </div>

                  {/* Vertical Lines */}
                  <div className="absolute inset-0 flex ml-16 pointer-events-none" data-id="element-491">
                    {scheduleData.map((_, i) => <div key={i} className="flex-1 border-r border-gray-100 last:border-r-0" data-id="element-492"></div>)}
                  </div>

                  {/* Events */}
                  <div className="absolute inset-0 ml-16 flex" data-id="element-493">
                    {scheduleData.map(day => <div key={day.date} className="flex-1 relative" data-id="element-494">
                        {day.events.map(event => {
                    const style = getPositionStyle(event.timeStart, event.timeEnd);
                    const bgClass = getStatusColor(event.status);
                    return <div key={event.id} className={`absolute inset-x-1 rounded-md border p-1.5 text-xs cursor-pointer hover:shadow-md transition-all z-10 overflow-hidden flex flex-col gap-0.5 ${bgClass}`} style={style} onClick={() => setSelectedEvent(event)} data-id="element-495">
                              <div className="flex items-center justify-between" data-id="element-496">
                                <span className="font-bold truncate" data-id="element-497">
                                  {event.timeStart} - {event.timeEnd}
                                </span>
                                <div className={`w-2 h-2 rounded-full ${getStatusDot(event.status)}`} data-id="element-498" />
                              </div>
                              <div className="font-medium truncate" data-id="element-499">
                                {event.zone}
                              </div>
                              <div className="truncate opacity-80 flex items-center gap-1" data-id="element-500">
                                <Sparkles size={10} data-id="element-501" />
                                {event.service}
                              </div>
                            </div>;
                  })}
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-600" data-id="element-502">
            <div className="flex items-center gap-2" data-id="element-503">
              <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-504"></div>
              <span data-id="element-505">Confermato</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-506">
              <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-507"></div>
              <span data-id="element-508">Da completare oggi</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-509">
              <div className="w-3 h-3 rounded-full bg-gray-400" data-id="element-510"></div>
              <span data-id="element-511">Completato</span>
            </div>
          </div>
        </> /* List View */ : <div className="space-y-6" data-id="element-512">
          {scheduleData.map(day => day.events.length > 0 && <div key={day.date} data-id="element-513">
                  <h3 className="text-sm font-bold text-gray-500 mb-3 uppercase" data-id="element-514">
                    {day.dayName} {day.dayNumber} Gennaio
                  </h3>
                  <div className="space-y-3" data-id="element-515">
                    {day.events.map(event => <div key={event.id} onClick={() => setSelectedEvent(event)} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-516">
                        <div className="flex items-start gap-4" data-id="element-517">
                          <div className="flex flex-col items-center justify-center w-14 h-14 bg-gray-50 rounded-lg border border-gray-100 flex-shrink-0" data-id="element-518">
                            <span className="text-xs text-gray-500 font-medium" data-id="element-519">
                              Inizio
                            </span>
                            <span className="text-sm font-bold text-navy" data-id="element-520">
                              {event.timeStart}
                            </span>
                          </div>
                          <div data-id="element-521">
                            <div className="flex items-center gap-2 mb-1" data-id="element-522">
                              <h4 className="font-bold text-navy" data-id="element-523">
                                {event.zone}
                              </h4>
                              {getStatusBadge(event.status)}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1" data-id="element-524">
                              <Sparkles size={14} className="text-teal-deep" data-id="element-525" />
                              <span data-id="element-526">{event.service}</span>
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-527">
                              Durata: {event.duration} • Fine prevista:{' '}
                              {event.timeEnd}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-end" data-id="element-528">
                          <ChevronRight size={20} className="text-gray-400" data-id="element-529" />
                        </div>
                      </div>)}
                  </div>
                </div>)}
          {scheduleData.every(d => d.events.length === 0) && <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300" data-id="element-530">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" data-id="element-531" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-532">
                Nessun intervento
              </h3>
              <p className="text-gray-500" data-id="element-533">
                Non ci sono interventi programmati per questa settimana.
              </p>
            </div>}
        </div>}

      {/* Event Details Modal */}
      {selectedEvent && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedEvent(null)} data-id="element-534">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()} data-id="element-535">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-536">
              <div data-id="element-537">
                <h3 className="font-bold text-navy" data-id="element-538">Dettaglio Intervento</h3>
                <p className="text-xs text-gray-500" data-id="element-539">{selectedEvent.reqId}</p>
              </div>
              <button onClick={() => setSelectedEvent(null)} className="p-1 hover:bg-gray-100 rounded-full" data-id="element-540">
                <X size={20} className="text-gray-500" data-id="element-541" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-542">
              <div className="flex items-start gap-4" data-id="element-543">
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0" data-id="element-544">
                  <MapPin size={20} className="text-teal-deep" data-id="element-545" />
                </div>
                <div data-id="element-546">
                  <div className="text-xs text-gray-500 font-medium" data-id="element-547">Zona</div>
                  <div className="font-semibold text-navy text-lg" data-id="element-548">
                    {selectedEvent.zone}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-549">
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0" data-id="element-550">
                  <Clock size={20} className="text-teal-deep" data-id="element-551" />
                </div>
                <div data-id="element-552">
                  <div className="text-xs text-gray-500 font-medium" data-id="element-553">
                    Orario
                  </div>
                  <div className="font-semibold text-navy" data-id="element-554">
                    {selectedEvent.timeStart} - {selectedEvent.timeEnd}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-555">
                    Durata: {selectedEvent.duration}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-556">
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0" data-id="element-557">
                  <Sparkles size={20} className="text-teal-deep" data-id="element-558" />
                </div>
                <div data-id="element-559">
                  <div className="text-xs text-gray-500 font-medium" data-id="element-560">
                    Servizio
                  </div>
                  <div className="font-semibold text-navy" data-id="element-561">
                    {selectedEvent.service}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-2" data-id="element-562">
                <div className="text-xs text-gray-500 font-medium w-10 text-center" data-id="element-563">
                  Stato
                </div>
                {getStatusBadge(selectedEvent.status)}
              </div>

              <div className="pt-4 flex gap-3" data-id="element-564">
                {(selectedEvent.status === 'confermato' || selectedEvent.status === 'oggi') && <button onClick={() => {
              setShowCompleteModal(true);
              // Keep selectedEvent for context
            }} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors shadow-sm" data-id="element-565">
                    Segna completato
                  </button>}

                {selectedEvent.status !== 'completato' && <button onClick={() => {
              setShowCancelModal(true);
              // Keep selectedEvent for context
            }} className="px-4 py-2.5 bg-white border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors" data-id="element-566">
                    Annulla intervento
                  </button>}
              </div>
            </div>
          </div>
        </div>}

      {/* Cancellation Modal */}
      {showCancelModal && <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-id="element-567">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200" data-id="element-568">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-red-50" data-id="element-569">
              <div className="flex items-center gap-2 text-red-700" data-id="element-570">
                <AlertCircle size={20} data-id="element-571" />
                <h3 className="font-bold" data-id="element-572">Annulla intervento</h3>
              </div>
              <button onClick={() => setShowCancelModal(false)} className="p-1 hover:bg-red-100 rounded-full" data-id="element-573">
                <X size={20} className="text-red-500" data-id="element-574" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-575">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-3" data-id="element-576">
                <AlertCircle className="text-amber-600 flex-shrink-0" size={20} data-id="element-577" />
                <p className="text-sm text-amber-800" data-id="element-578">
                  La cancellazione potrebbe influire sul tuo rating e i crediti
                  NON verranno rimborsati.
                </p>
              </div>

              <div data-id="element-579">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-580">
                  Motivazione
                </label>
                <select className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none bg-white" value={cancelReason} onChange={e => setCancelReason(e.target.value)} data-id="element-581">
                  <option value="" data-id="element-582">Seleziona motivo...</option>
                  <option value="imprevisto" data-id="element-583">Imprevisto personale</option>
                  <option value="logistica" data-id="element-584">Problemi logistici</option>
                  <option value="veicolo" data-id="element-585">Indisponibilità veicolo</option>
                  <option value="malattia" data-id="element-586">Malattia</option>
                  <option value="altro" data-id="element-587">Altro</option>
                </select>
              </div>

              <div data-id="element-588">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-589">
                  Note aggiuntive
                </label>
                <textarea className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none" rows={3} placeholder="Aggiungi dettagli (opzionale)" value={cancelNotes} onChange={e => setCancelNotes(e.target.value)} data-id="element-590"></textarea>
              </div>

              <div className="flex gap-3 pt-2" data-id="element-591">
                <button onClick={() => setShowCancelModal(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors" data-id="element-592">
                  Annulla
                </button>
                <button className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" disabled={!cancelReason} onClick={() => {
              // Handle cancellation logic here
              setShowCancelModal(false);
              setSelectedEvent(null);
              setCancelReason('');
              setCancelNotes('');
            }} data-id="element-593">
                  Conferma cancellazione
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Completion Modal */}
      {showCompleteModal && selectedEvent && <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-id="element-594">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200" data-id="element-595">
            <div className="p-6 text-center space-y-4" data-id="element-596">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-597">
                <CheckCircle size={32} className="text-green-600" data-id="element-598" />
              </div>

              <h3 className="text-xl font-bold text-navy" data-id="element-599">
                Completa Intervento
              </h3>

              <p className="text-gray-600" data-id="element-600">
                Confermi di aver completato l'intervento{' '}
                <span className="font-semibold text-navy" data-id="element-601">
                  {selectedEvent.reqId}
                </span>
                ?
              </p>

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:bg-gray-50 transition-colors cursor-pointer group" data-id="element-602">
                <div className="flex flex-col items-center gap-2" data-id="element-603">
                  <div className="p-3 bg-gray-100 rounded-full group-hover:bg-white transition-colors" data-id="element-604">
                    <Camera size={24} className="text-gray-500" data-id="element-605" />
                  </div>
                  <div className="text-sm font-medium text-gray-700" data-id="element-606">
                    Carica una foto (opzionale)
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-607">
                    Prova del lavoro svolto
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4" data-id="element-608">
                <button onClick={() => setShowCompleteModal(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors" data-id="element-609">
                  Annulla
                </button>
                <button className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors shadow-sm" onClick={() => {
              // Handle completion logic here
              setShowCompleteModal(false);
              setSelectedEvent(null);
            }} data-id="element-610">
                  Conferma completamento
                </button>
              </div>
            </div>
          </div>
        </div>}
    </MobileLayout>;
}